import React, { createContext, useContext, useEffect, useState } from "react";

const ThemeContext = createContext();

// Available theme variants
export const THEMES = {
  default: {
    name: "Default",
    colors: {
      primary: "#2563eb",
      secondary: "#e5e7eb",
      success: "#10b981",
      error: "#ef4444",
      warning: "#f59e0b",
      info: "#3b82f6",
    },
  },
  ocean: {
    name: "Ocean",
    colors: {
      primary: "#0369a1",
      secondary: "#cffafe",
      success: "#06b6d4",
      error: "#f97316",
      warning: "#eab308",
      info: "#0284c7",
    },
  },
  forest: {
    name: "Forest",
    colors: {
      primary: "#059669",
      secondary: "#d1fae5",
      success: "#10b981",
      error: "#dc2626",
      warning: "#d97706",
      info: "#047857",
    },
  },
  sunset: {
    name: "Sunset",
    colors: {
      primary: "#dc2626",
      secondary: "#fed7aa",
      success: "#16a34a",
      error: "#991b1b",
      warning: "#d97706",
      info: "#ea580c",
    },
  },
  purple: {
    name: "Purple",
    colors: {
      primary: "#7c3aed",
      secondary: "#e9d5ff",
      success: "#06b6d4",
      error: "#ec4899",
      warning: "#f59e0b",
      info: "#8b5cf6",
    },
  },
};

// ThemeProvider component. When `mode` is passed (App.js passes the app's own
// Light/Dark toggle), it is the single source of truth for the html `.dark`
// class -- otherwise a dark OS setting kept `.dark` on while the app was in
// Light mode, so text-foreground headings rendered near-white on cream.
export function ThemeProvider({ children, mode: controlledMode, onToggleMode }) {
  const [ownMode, setMode] = useState(() => {
    // Check localStorage first
    const stored = localStorage.getItem("theme-mode");
    if (stored) return stored;
    
    // Check system preference
    if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) {
      return "dark";
    }
    return "light";
  });

  const mode = controlledMode || ownMode;

  const [theme, setTheme] = useState(() => {
    return localStorage.getItem("theme-name") || "default";
  });

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    
    // Set dark/light mode
    if (mode === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    
    // Set theme colors as CSS variables
    const colors = THEMES[theme]?.colors || THEMES.default.colors;
    Object.entries(colors).forEach(([key, value]) => {
      root.style.setProperty(`--rp-color-${key}`, value);
    });

    // Persist preferences
    if (!controlledMode) localStorage.setItem("theme-mode", mode);
    localStorage.setItem("theme-name", theme);
  }, [mode, theme, controlledMode]);

  const toggleMode = () => {
    if (controlledMode) onToggleMode?.();
    else setMode((prev) => (prev === "dark" ? "light" : "dark"));
  };

  const setThemeName = (name) => {
    if (THEMES[name]) {
      setTheme(name);
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        mode,
        toggleMode,
        theme,
        setThemeName,
        themes: THEMES,
        colors: THEMES[theme]?.colors || THEMES.default.colors,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
}
